/** Sprint 5-c · 公司會議室 · 會議列表（行政中心 · 已排/進行中/已完成）。 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, Paper, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import GroupsIcon from '@mui/icons-material/Groups';

import { useMessage } from '../../../contexts/MessageContext';
import { usePermission } from '../../../hooks/usePermission';
import { formatLocal } from '../../../utils/dateHelper';
import { listMeetings, OfficeMeeting } from './services/officeMeetingService';

const FUNC = 'ee_office_meeting';

const STATUS_COLOR: Record<string, 'default' | 'primary' | 'success' | 'warning'> = {
  scheduled: 'primary', in_progress: 'warning', ended: 'success', cancelled: 'default',
};

const OfficeMeetingsPage: React.FC = () => {
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`officeMeeting.${k}`, d);
  const { showApiError } = useMessage();
  const { hasPermission } = usePermission();
  const [rows, setRows] = useState<OfficeMeeting[]>([]);
  const [tab, setTab] = useState<'active' | 'ended' | 'cancelled'>('active');

  const load = useCallback(async () => {
    try { setRows(await listMeetings()); } catch (err) { showApiError(err); }
  }, [showApiError]);
  useEffect(() => { load(); }, [load]);

  const statusLabel = (s: string) => tt(`status_${s}`, s);

  const filtered = useMemo(() => rows.filter((m) => {
    if (tab === 'active') return m.status === 'scheduled' || m.status === 'in_progress';
    if (tab === 'ended') return m.status === 'ended';
    return m.status === 'cancelled';
  }), [rows, tab]);

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5"><GroupsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />{tt('title', '公司會議室')}</Typography>
        {hasPermission(FUNC, 'create') && (
          /* H.4：建立會議改開日曆的會議表單（預設勾好「這是會議」）*/
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => nav('/ee_my_calendar?new=meeting')}>
            {tt('create', '建立會議')}</Button>
        )}
      </Stack>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab value="active" label={tt('tab_active', '已排/進行中')} />
        <Tab value="ended" label={tt('tab_ended', '已完成')} />
        <Tab value="cancelled" label={tt('tab_cancelled', '已取消')} />
      </Tabs>

      <Stack spacing={1}>
        {filtered.length === 0 && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography color="text.secondary">{tt('empty', '沒有會議。')}</Typography>
          </Paper>
        )}
        {filtered.map((m) => (
          <Paper key={m.id} variant="outlined" sx={{ p: 2, cursor: 'pointer' }}
            onClick={() => nav(`/ee_office_meeting/${m.id}`)}>
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1">{m.title}</Typography>
                  <Chip size="small" color={STATUS_COLOR[m.status]} label={statusLabel(m.status)} />
                  {m.public_meeting === false && (
                    <Chip size="small" color="default" variant="outlined"
                      label={`🔒 ${tt('secret', '秘密')}`} />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block">
                  {m.meeting_no} · {formatLocal(m.start_at)} ~ {formatLocal(m.end_at)}
                  {m.location ? ` · ${m.location}` : ''} · {tt('owner', '主辦')}：{m.owner_name}</Typography>
              </Box>
              {(m.status === 'scheduled' || m.status === 'in_progress') && (
                <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); nav(`/ee_office_meeting/${m.id}/room`); }}>
                  {tt('enterRoom', '進入會議室')}</Button>
              )}
              {m.status === 'ended' && (
                <Button size="small" onClick={(e) => { e.stopPropagation(); nav(`/ee_office_meeting/${m.id}/minutes`); }}>
                  {tt('viewMinutes', '會議記錄')}</Button>
              )}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default OfficeMeetingsPage;
